import CloseIcons from '@/icons/CloseIcons'
import DownIcons from '@/icons/DownIcons'
import {
    CustomerEventWarning,
    LeadWarning,
    WarningItem,
} from '@/modules/Util/components/Alerts/types'
import { useQuery } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useEffect, useRef, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Loading, Typogaphy, WarningIcons } from 'vnetwork-ui'
import AlertDetail from './AlertDetail'
import useAuthStore from '@/stores/useAuthStore'
import usePermissionsStore from '@/stores/usePermissionsStore'
import { resources, scopes } from '@/utils/permission'
import {
    fetchWarningsForScopes,
    getScopesByDepartment,
    WARNING_TYPE_ORDER,
} from './services'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

// Chỉ hiển thị cảnh báo lead (chưa nhận / chưa phân loại) cho lead có ngày
// từ 01/01/2026 trở đi — ẩn lead cũ. Mốc ngày lấy từ warning.created_at:
// unassigned = ngày tạo lead, stale = ngày được gán.
const LEAD_ALERT_START_DATE = new Date('2026-01-01T00:00:00')

// Nhóm lead luôn hiển thị (kể cả khi rỗng sau khi lọc) → cần title fallback
// vì nhóm rỗng không có item để lấy title từ backend.
const LEAD_GROUP_FALLBACK_TITLE: Record<string, string> = {
    lead_unassigned: 'Leads chưa nhận > 24h',
    lead_stale: 'Leads chưa phân loại > 24h',
}

const AlertNotification: React.FC = () => {
    const { employee } = useAuthStore()
    const { permissions, isAdmin } = usePermissionsStore()
    const { t } = useTranslation()
    const [isOpen, setIsOpen] = useState(false)
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
    const alertRef = useRef<HTMLDivElement>(null)
    const reportedFailureRef = useRef<string | null>(null)

    const canViewCustomerEvents = useMemo(
        () =>
            isAdmin ||
            permissions.some(
                (permission) =>
                    permission.rsname === resources.customers &&
                    permission.scopes?.includes(scopes.create)
            ),
        [isAdmin, permissions]
    )

    // Department mappings intentionally cover all legacy warning sources only.
    // Customer events are granted solely by Admin or Customers:create.
    const allowedScopes = useMemo(() => {
        const departmentScopes = employee?.department_name
            ? getScopesByDepartment(employee.department_name)
            : []
        return canViewCustomerEvents
            ? [...new Set([...departmentScopes, 'customer_event_upcoming'])]
            : departmentScopes
    }, [canViewCustomerEvents, employee?.department_name])

    // Fetch warnings data based on allowed scopes
    const {
        data: warningResult,
        isLoading,
        isError,
        error,
    } = useQuery({
        queryKey: [QUERY_KEYS.WARNINGS, allowedScopes],
        queryFn: () => fetchWarningsForScopes(allowedScopes),
        refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
        retry: 2,
        staleTime: 5 * 60 * 1000,
        enabled: allowedScopes.length > 0, // Only fetch if user has allowed scopes
    })

    const alerts = warningResult?.warnings ?? []
    const failedSources = warningResult?.failedSources ?? []

    useEffect(() => {
        const signature = isError
            ? `all:${error instanceof Error ? error.message : String(error)}`
            : failedSources.length > 0
              ? `partial:${failedSources.join(',')}`
              : null

        if (!signature) {
            reportedFailureRef.current = null
            return
        }
        if (reportedFailureRef.current === signature) return

        reportedFailureRef.current = signature
        if (isError) {
            Sentry.captureException(error, {
                extra: { warningFailureSignature: signature },
            })
        } else {
            Sentry.captureMessage(
                'Alert notification sources partially failed',
                {
                    level: 'warning',
                    extra: { failedSources },
                }
            )
        }
    }, [error, failedSources, isError])

    // Lọc cảnh báo lead theo mốc 01/01/2026 (chỉ lead từ 2026 trở đi).
    // Lọc tại nguồn để badge count và danh sách luôn khớp nhau.
    const filteredAlerts = useMemo(() => {
        return (alerts ?? []).filter((alert) => {
            if (
                alert.type === 'lead_unassigned' ||
                alert.type === 'lead_stale'
            ) {
                const d = new Date(alert.created_at)
                return !isNaN(d.getTime()) && d >= LEAD_ALERT_START_DATE
            }
            return true
        })
    }, [alerts])

    // Close popup when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                alertRef.current &&
                !alertRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false)
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isOpen])

    const getAlertIcon = (type: WarningItem['type']) => {
        switch (type) {
            case 'contract_expired':
                return '🚨'
            case 'contract_expiring':
                return '📋'
            case 'dayoff_pending':
                return '⏳'
            case 'document_retrieval_upcoming':
                return '📄'
            case 'document_retrieval_overdue':
                return '📄'
            case 'lead_unassigned':
                return '📥'
            case 'lead_stale':
                return '⏰'
            case 'ticket_escalated':
                return '🔺'
            case 'customer_event_upcoming':
                return '🎂'
            default:
                return '⚠️'
        }
    }

    // Group alerts by type and sort according to priority order
    const groupedAlerts = useMemo(() => {
        const groups: Record<string, WarningItem[]> = {}
        filteredAlerts.forEach((alert) => {
            if (!groups[alert.type]) {
                groups[alert.type] = []
            }
            groups[alert.type].push(alert)
        })

        // Luôn giữ nhóm lead (nếu thuộc scope) kể cả khi rỗng — để mục
        // "Leads chưa nhận > 24h" / "Leads chưa phân loại > 24h" vẫn hiển thị
        // (đếm = 0) sau khi đã lọc bỏ lead cũ trước 2026.
        const alwaysShowLeadTypes = ['lead_unassigned', 'lead_stale']
        alwaysShowLeadTypes.forEach((type) => {
            if (allowedScopes.includes(type) && !groups[type]) {
                groups[type] = []
            }
        })

        // Sort each group
        Object.keys(groups).forEach((type) => {
            if (type === 'lead_unassigned' || type === 'lead_stale') {
                // Lead alerts: sắp theo thời gian chờ TĂNG DẦN — lead vừa qua
                // ngưỡng 24h đứng đầu, lead chờ lâu hơn xuống dưới.
                groups[type].sort((a, b) => {
                    const aDays = (a.data as LeadWarning)?.days_pending ?? 0
                    const bDays = (b.data as LeadWarning)?.days_pending ?? 0
                    return aDays - bDays
                })
            } else {
                // Các cảnh báo khác: giữ nguyên — sort theo severity (high trước)
                groups[type].sort((a, b) => {
                    const severityOrder = { high: 0, medium: 1, low: 2 }
                    return severityOrder[a.severity] - severityOrder[b.severity]
                })
            }
        })

        // Sort groups by WARNING_TYPE_ORDER
        const sortedGroups: Record<string, WarningItem[]> = {}
        WARNING_TYPE_ORDER.forEach((type) => {
            if (groups[type]) {
                sortedGroups[type] = groups[type]
            }
        })

        // Add any remaining types not in the order
        Object.keys(groups).forEach((type) => {
            if (!sortedGroups[type]) {
                sortedGroups[type] = groups[type]
            }
        })

        return sortedGroups
    }, [filteredAlerts, allowedScopes])

    const toggleGroup = (type: string) => {
        const newExpandedGroups = new Set(expandedGroups)
        if (newExpandedGroups.has(type)) {
            newExpandedGroups.delete(type)
        } else {
            newExpandedGroups.add(type)
        }
        setExpandedGroups(newExpandedGroups)
    }

    const highPriorityCount = filteredAlerts.filter(
        (alert) => alert.severity === 'high'
    ).length
    const totalCount = filteredAlerts.length

    const getAlertKey = (alert: WarningItem) => {
        if (alert.type === 'customer_event_upcoming') {
            const event = alert.data as CustomerEventWarning
            return (
                event.event_key ||
                `${alert.type}-${event.customer_id}-${event.event_type}-${event.event_month}-${event.event_day}`
            )
        }
        return `${alert.type}-${alert.id}`
    }

    // Show component only if user has allowed scopes
    if (allowedScopes.length === 0) {
        return null
    }

    return (
        <div
            className="relative"
            aria-labelledby="UI:Alert-Notification"
            ref={alertRef}
        >
            <motion.div
                className="relative cursor-pointer p-2 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={() => setIsOpen(!isOpen)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
            >
                {/* Alert Icon */}
                <div className="relative">
                    <svg
                        className={`w-6 h-6 ${highPriorityCount && highPriorityCount > 0 ? 'text-c-error' : 'text-gray-600'}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 16.5C3.544 18.333 4.506 20 6.046 20z"
                        />
                    </svg>

                    {/* Badge */}
                    {totalCount && totalCount > 0 ? (
                        <motion.div
                            className={`absolute -top-2 -right-2 min-w-[20px] h-5 rounded-full flex items-center justify-center text-xs font-bold text-white shadow-lg ${
                                highPriorityCount && highPriorityCount > 0
                                    ? 'bg-red-600'
                                    : 'bg-blue-500'
                            }`}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{
                                type: 'spring',
                                stiffness: 500,
                                damping: 30,
                            }}
                        >
                            {totalCount > 99 ? '99+' : totalCount}
                        </motion.div>
                    ) : null}

                    {(isError || failedSources.length > 0) && (
                        <span
                            className={`absolute -bottom-1 -right-1 h-3 w-3 rounded-full border-2 border-white ${
                                isError ? 'bg-red-600' : 'bg-amber-500'
                            }`}
                            title={t(
                                isError
                                    ? 'customer:calendar_events.alert_load_error'
                                    : 'customer:calendar_events.alert_partial'
                            )}
                            aria-label={t(
                                isError
                                    ? 'customer:calendar_events.alert_load_error'
                                    : 'customer:calendar_events.alert_partial'
                            )}
                        />
                    )}
                </div>

                {/* Pulsing effect for high priority alerts */}
                {highPriorityCount && highPriorityCount > 0 ? (
                    <motion.div
                        className="absolute inset-0 rounded-lg bg-red-400"
                        animate={{
                            opacity: [0, 0.3, 0],
                            scale: [1, 1.1, 1],
                        }}
                        transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: 'easeInOut',
                        }}
                    />
                ) : null}
            </motion.div>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        className="fixed right-4 top-16 w-1/2 xs:w-full bg-white rounded-lg shadow-xl border border-gray-200 max-h-96 overflow-hidden"
                        style={{ zIndex: 99999 }}
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                    >
                        <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-red-50 to-orange-50">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                                        <WarningIcons className="text-c-error w-5 h-5" />
                                    </div>
                                    <div>
                                        <Typogaphy className="font-semibold text-c-error uppercase">
                                            {t('alert-notification')}
                                        </Typogaphy>
                                        <Typogaphy className="text-sm">
                                            {t('alert-notification-count', {
                                                totalCount,
                                            })}
                                        </Typogaphy>
                                    </div>
                                </div>
                                <CloseIcons
                                    className="cursor-pointer w-5 h-5 text-gray-400 hover:text-gray-600 transition-colors"
                                    onClick={() => setIsOpen(false)}
                                />
                            </div>
                        </div>

                        {/* Alert List */}
                        <div className="max-h-80 overflow-y-auto">
                            {isLoading ? (
                                <div className="p-8 flex justify-center">
                                    <Loading.BarWave size={40} />
                                </div>
                            ) : isError ? (
                                <div className="p-8 text-center text-sm text-red-600">
                                    {t(
                                        'customer:calendar_events.alert_load_error'
                                    )}
                                </div>
                            ) : (
                                <>
                                    {failedSources.length > 0 && (
                                        <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
                                            {t(
                                                'customer:calendar_events.alert_partial',
                                                { count: failedSources.length }
                                            )}
                                        </div>
                                    )}
                                    {Object.keys(groupedAlerts).length === 0 ? (
                                        failedSources.length > 0 ? (
                                            <div className="p-8 text-center text-sm text-amber-800">
                                                {t(
                                                    'customer:calendar_events.alert_partial_empty'
                                                )}
                                            </div>
                                        ) : (
                                            <div className="p-8 text-center">
                                                <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                                                    <span className="text-green-600 text-2xl">
                                                        ✓
                                                    </span>
                                                </div>
                                                <Typogaphy className="text-gray-600">
                                                    {t('no-alert-to-process')}
                                                </Typogaphy>
                                            </div>
                                        )
                                    ) : (
                                        Object.entries(groupedAlerts).map(
                                            ([type, alerts]) => (
                                                <div
                                                    key={type}
                                                    className="border-b border-gray-200 last:border-b-0"
                                                >
                                                    {/* Group Header */}
                                                    <div
                                                        className="p-3 bg-gray-50 border-b border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors"
                                                        onClick={() =>
                                                            toggleGroup(type)
                                                        }
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center gap-3">
                                                                <div
                                                                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                                                        type ===
                                                                        'contract_expired'
                                                                            ? 'bg-red-100'
                                                                            : type ===
                                                                                'contract_expiring'
                                                                              ? 'bg-yellow-100'
                                                                              : 'bg-blue-100'
                                                                    }`}
                                                                >
                                                                    <span
                                                                        className={`text-lg ${
                                                                            type ===
                                                                            'contract_expired'
                                                                                ? 'text-red-600'
                                                                                : type ===
                                                                                    'contract_expiring'
                                                                                  ? 'text-yellow-600'
                                                                                  : 'text-blue-600'
                                                                        }`}
                                                                    >
                                                                        {getAlertIcon(
                                                                            type as any
                                                                        )}
                                                                    </span>
                                                                </div>
                                                                <div>
                                                                    <Typogaphy className="font-medium text-primary capitalize">
                                                                        {type ===
                                                                        'customer_event_upcoming'
                                                                            ? t(
                                                                                  'customer:calendar_events.alert_title'
                                                                              )
                                                                            : t(
                                                                                  `alerts.type.${type}`,
                                                                                  {
                                                                                      defaultValue:
                                                                                          alerts[0]
                                                                                              ?.title ??
                                                                                          LEAD_GROUP_FALLBACK_TITLE[
                                                                                              type
                                                                                          ] ??
                                                                                          type,
                                                                                  }
                                                                              )}
                                                                    </Typogaphy>
                                                                    <Typogaphy className="text-sm text-gray-500">
                                                                        {
                                                                            alerts.length
                                                                        }{' '}
                                                                        {t(
                                                                            'alerts.items'
                                                                        )}
                                                                    </Typogaphy>
                                                                </div>
                                                            </div>
                                                            <motion.div
                                                                animate={{
                                                                    rotate: expandedGroups.has(
                                                                        type
                                                                    )
                                                                        ? 180
                                                                        : 0,
                                                                }}
                                                                transition={{
                                                                    duration: 0.2,
                                                                }}
                                                            >
                                                                <DownIcons className="w-5 h-5 text-gray-400" />
                                                            </motion.div>
                                                        </div>
                                                    </div>

                                                    {/* Group Content - Always show collapsed by default */}
                                                    <AnimatePresence>
                                                        {expandedGroups.has(
                                                            type
                                                        ) && (
                                                            <motion.div
                                                                initial={{
                                                                    height: 0,
                                                                    opacity: 0,
                                                                }}
                                                                animate={{
                                                                    height: 'auto',
                                                                    opacity: 1,
                                                                }}
                                                                exit={{
                                                                    height: 0,
                                                                    opacity: 0,
                                                                }}
                                                                transition={{
                                                                    duration: 0.3,
                                                                }}
                                                                className="overflow-hidden"
                                                            >
                                                                <div className="flex flex-col gap-1 ps-14">
                                                                    {alerts.map(
                                                                        (
                                                                            alert,
                                                                            index
                                                                        ) => (
                                                                            <AlertDetail
                                                                                key={getAlertKey(
                                                                                    alert
                                                                                )}
                                                                                alert={
                                                                                    alert
                                                                                }
                                                                                index={
                                                                                    index
                                                                                }
                                                                            />
                                                                        )
                                                                    )}
                                                                </div>
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                </div>
                                            )
                                        )
                                    )}
                                </>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

export default AlertNotification
